import { Component, Input, OnDestroy } from '@angular/core';
import { UserStatusService } from '../header/statistics-dropdown/user-status.service';
import { resolveProfileImageUrl } from 'src/app/utils/utils.function';

/**
 * Wraps app-profile-avatar with a floating card, shown on hover, carrying whatever
 * profile fields the passed-in user object has (role/email/status are all optional
 * upstream - staff/vendor/customer payloads don't share a fixed shape).
 */
@Component({
  selector: 'app-user-hover-card',
  templateUrl: './user-hover-card.component.html',
  styleUrls: ['./user-hover-card.component.scss'],
  standalone: false,
})
export class UserHoverCardComponent implements OnDestroy {
  @Input() user: any = null;
  @Input() avatarSize: string = '32px';

  showCard = false;
  cardPosition = { x: 0, y: 0 };

  private openTimer: any = null;
  private closeTimer: any = null;
  private readonly OPEN_DELAY = 200;
  private readonly CLOSE_DELAY = 100;
  private scrollListener = () => this.hideCardImmediately();

  constructor(public userStatusService: UserStatusService) {}

  get fullName(): string {
    return this.user?.full_name || this.user?.name || '';
  }

  get profileUrl(): string {
    return resolveProfileImageUrl(this.user?.profile_image) || this.user?.avatar || '';
  }

  get statusSlug(): string | null {
    return this.user?.user_status?.value || null;
  }

  get statusLabel(): string | undefined {
    return this.statusSlug ? this.userStatusService.getStatusBySlug(this.statusSlug)?.title : undefined;
  }

  get roleName(): string | null {
    return this.user?.role?.name || this.user?.title || null;
  }

  onMouseEnter(event: MouseEvent): void {
    clearTimeout(this.closeTimer);
    const target = event.currentTarget as HTMLElement;
    this.openTimer = setTimeout(() => {
      this.positionCard(target);
      this.showCard = true;
      document.addEventListener('scroll', this.scrollListener, true);
    }, this.OPEN_DELAY);
  }

  onMouseLeave(): void {
    clearTimeout(this.openTimer);
    this.closeTimer = setTimeout(() => this.hideCardImmediately(), this.CLOSE_DELAY);
  }

  cancelHide(): void {
    clearTimeout(this.closeTimer);
  }

  private positionCard(target: HTMLElement): void {
    const rect = target.getBoundingClientRect();
    const cardWidth = 280;
    const cardHeight = 160;
    const margin = 8;

    let x = rect.left;
    let y = rect.bottom + margin;

    if (x + cardWidth > window.innerWidth - 20) {
      x = window.innerWidth - cardWidth - 20;
    }
    if (y + cardHeight > window.innerHeight - 20) {
      y = rect.top - cardHeight - margin;
    }

    this.cardPosition = { x, y };
  }

  private hideCardImmediately(): void {
    this.showCard = false;
    document.removeEventListener('scroll', this.scrollListener, true);
  }

  ngOnDestroy(): void {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    document.removeEventListener('scroll', this.scrollListener, true);
  }
}
